import React, { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { Spinner } from "reactstrap";
import Swal from "sweetalert2";
import { Button, Icon } from "../../../components/Component";
import getTextColor from "../../../components/custom/getTextColor";
import { servicePutLivechatTags } from "../../../services/livechat";

const ERROR_TEXT = {
  "error.livechat.invalid_tags": "Tag ini milik department lain.",
  "error.livechat.no_ticket": "Percakapan ini belum punya tiket.",
};

// one tag per category, the same rule as the WhatsApp tag modal
export const pickTag = (selected, tag) => {
  if (selected.some((item) => item.id === tag.id)) return selected.filter((item) => item.id !== tag.id);

  return [...selected.filter((item) => item.tagCategoryId !== tag.tagCategoryId), tag];
};

const groupByCategory = (tags) =>
  Object.values(
    tags.reduce((groups, tag) => {
      const key = tag.tagCategoryId ?? "none";
      const group = groups[key] ?? { id: key, name: tag.tagCategory?.name ?? "Lainnya", tags: [] };
      return { ...groups, [key]: { ...group, tags: [...group.tags, tag] } };
    }, {})
  );

const LivechatModalTag = ({ isOpen, conversationId, departmentId, isResolved, tags, currentTagIds, onClose, onSaved }) => {
  const [cookies] = useCookies(["SIDikey"]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // reset only when the modal opens, so a socket re-render never drops the agent's picks
  useEffect(() => {
    if (isOpen) {
      setSelected(tags.filter((tag) => currentTagIds.includes(tag.id)));
      setSearch("");
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const allowed = tags.filter(
      (tag) =>
        (!tag.departments?.length || tag.departments.some((dept) => dept.id === departmentId)) &&
        tag.name?.toLowerCase().includes(query)
    );
    return groupByCategory(allowed);
  }, [tags, departmentId, search]);

  const save = async () => {
    setIsSaving(true);
    const res = await servicePutLivechatTags(
      cookies.SIDikey,
      conversationId,
      selected.map((tag) => tag.id)
    );
    setIsSaving(false);

    if (res.status === 200) {
      onSaved(selected);
      return;
    }

    Swal.fire({ icon: "error", title: "Gagal menyimpan tag", text: ERROR_TEXT[res.data?.message] ?? "Coba lagi." });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-tag-main">
      <div className="modal-tag-overlay" onClick={onClose} />
      <div className="modal-tag-body px-2 pt-3 pb-2" role="dialog" aria-label="Tag tiket">
        <button type="button" className="close btn btn-icon btn-trigger" aria-label="Tutup" onClick={onClose}>
          <Icon name="cross-sm" />
        </button>
        {isResolved ? (
          <>
            <h5 className="resolve-prevent">Chat dalam keadaan resolved.</h5>
            <div className="mb-1" />
          </>
        ) : (
          <>
            <div className="mb-4" />
            <div className="form-control-wrap">
              <div className="form-icon form-icon-left">
                <Icon name="search" />
              </div>
              <input
                type="text"
                className="form-round form-control"
                placeholder="Search Tag Name"
                aria-label="Cari tag"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: 350, overflowY: "auto" }} className="mt-2 tag-assign">
              {groups.map((group) => (
                <React.Fragment key={group.id}>
                  <div className="text-black py-2 font-weight-bold">
                    <p>{group.name}</p>
                  </div>
                  {group.tags.map((tag) => (
                    <div className="card-inner-md" key={tag.id} style={{ cursor: "pointer" }}>
                      <div className="user-card">
                        <label className="mb-0">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={selected.some((item) => item.id === tag.id)}
                            onChange={() => setSelected((prev) => pickTag(prev, tag))}
                          />
                          <span className="badge rounded-pill" style={{ background: tag.color, color: getTextColor(tag.color) }}>
                            {tag.name}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
              {groups.length === 0 && <p className="sub-text py-2">Tidak ada tag</p>}
            </div>
            <Button
              color="primary"
              className="mt-2 mx-auto"
              style={{ width: "80%", justifyContent: "center" }}
              disabled={isSaving}
              onClick={save}
            >
              {isSaving ? <Spinner size="sm" /> : "Pilih"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default LivechatModalTag;
